// @ts-ignore
import { useEffect, useState, PropsWithChildren } from "react";

import { css } from "@compiled/react";
import { fetch } from "cross-fetch";
import { useIntl } from "react-intl";

import { Cookie } from "@/utils/cookie";
import { setLocalStorage, removeLocalStorage } from "@/utils/localstorage";

import { ContentHit } from "./auto-complete-search-bar";

export const ALGOLIA_APP_ID = process.env["ALGOLIA_APP_ID"] as string;
export const ALGOLIA_SEARCH_API_KEY = process.env[
    "ALGOLIA_SEARCH_API_KEY"
] as string;
export const ALGOLIA_FACET_KEY = "category";
export const ALGOLIA_SEARCH_INDEX_NAME_V3 = "www.atlassian.com.v3";
export const ALGOLIA_SEARCH_INDEX_NAME_V3_2 = "www.atlassian.com.v3-2";
export const ALGOLIA_DEV_SEARCH_INDEX_V3 = "www.atlassian.com.v3_dev";

export const activeSearchIndexV3 =
    process.env["NODE_ENV"] === "production"
        ? ALGOLIA_SEARCH_INDEX_NAME_V3
        : ALGOLIA_DEV_SEARCH_INDEX_V3;
export const activeSearchIndexV3_2 =
    process.env["NODE_ENV"] === "production"
        ? ALGOLIA_SEARCH_INDEX_NAME_V3_2
        : ALGOLIA_DEV_SEARCH_INDEX_V3;

export const AA_AUTOCOMPLETE_POTENTIAL_CONVERSION_EVENT_LOCAL_STORAGE_KEY =
    "aa-autocomplete-pce";

type sendConversionEventAlgoliaProps = {
    objectID: string;
    queryID: string;
    position: number;
    searchIndex?: string;
};

export async function sendConversionEventAlgolia({
    objectID,
    queryID,
    searchIndex = activeSearchIndexV3
}: sendConversionEventAlgoliaProps) {
    const algoliaApplicationID = ALGOLIA_APP_ID;
    const algoliaAdminAPIKey = ALGOLIA_SEARCH_API_KEY;
    const insightsURL = `https://insights.algolia.io/1/events`;
    const anonymousId = Cookie.readCookie("ajs_anonymous_id");
    let decodedAnonymousId;

    if (anonymousId) {
        // the cookie value is URI encoded with quotes on the outside, so decode and remove them
        decodedAnonymousId = decodeURIComponent(anonymousId).replace(
            /['"]+/g,
            ""
        );
    }

    const eventData = {
        events: [
            {
                eventType: "conversion",
                eventName: "Result clicked (global nav)",
                index: searchIndex,
                userToken: decodedAnonymousId,
                objectIDs: [objectID],
                queryID: queryID
            }
        ]
    };

    try {
        const response = await fetch(insightsURL, {
            method: "POST",
            headers: {
                "X-Algolia-Application-Id": algoliaApplicationID,
                "X-Algolia-API-Key": algoliaAdminAPIKey,
                "Content-Type": "text/plain;charset=UTF-8"
            },
            body: JSON.stringify(eventData)
        });

        if (!response.ok) {
            throw new Error(`Error: ${response.statusText}`);
        }
    } catch (error) {
        console.error("Failed to send event:", error);
    }
}

const removeConversionEventLocalStorageIfAlreadyExist = () => {
    if (
        localStorage.getItem(
            AA_AUTOCOMPLETE_POTENTIAL_CONVERSION_EVENT_LOCAL_STORAGE_KEY
        )
    ) {
        removeLocalStorage(
            AA_AUTOCOMPLETE_POTENTIAL_CONVERSION_EVENT_LOCAL_STORAGE_KEY
        );
    }
};

const setPotentialConversionEventLocalStorage = (
    stringifyConversionEventObject: string
) => {
    // reset localstorage if already exist
    removeConversionEventLocalStorageIfAlreadyExist();
    setLocalStorage(
        AA_AUTOCOMPLETE_POTENTIAL_CONVERSION_EVENT_LOCAL_STORAGE_KEY,
        stringifyConversionEventObject,
        3600 // per code comment, ttl in seconds for the localstorage. 3600 secs = 1hr
    );
};

// click events are tracked through the algolia client's
// built in "insights," but conversion events need to be
// handled manually.
export const handleAlgoliaConversionMetrics = (item: ContentHit) => {
    if (item.url.includes("/signup")) {
        removeConversionEventLocalStorageIfAlreadyExist();
        sendConversionEventAlgolia({
            objectID: item.objectID,
            queryID: item.queryID,
            position: item.position
        });
    } else if (item.url.includes("/software/")) {
        const stringifyAttributes = JSON.stringify({
            objectID: item.objectID,
            queryID: item.queryID,
            position: item.position
        });
        setPotentialConversionEventLocalStorage(stringifyAttributes);
    }
};

export interface UseA11yDropdownAnnouncementProps {
    searchTerm: string;
    resultsCount: number;
}
export function useA11yDropdownAnnouncement(
    props: UseA11yDropdownAnnouncementProps
) {
    const { resultsCount, searchTerm } = props;

    const [dropdownAnnouncement, setDropdownAnnouncement] = useState("");
    useEffect(() => {
        setDropdownAnnouncement("");
    }, [searchTerm]);

    const intl = useIntl();
    useEffect(() => {
        const timer = setTimeout(() => {
            setDropdownAnnouncement(
                intl.formatMessage(
                    {
                        id: "AutoCompleteSearchBar.dropdown.announcement",
                        defaultMessage:
                            '{resultsCount} results displayed for search-term "{searchTerm}"'
                    },
                    {
                        resultsCount,
                        searchTerm
                    }
                )
            );
        }, 2000);
        return () => clearTimeout(timer);
    }, [resultsCount, searchTerm]);

    return { dropdownAnnouncement };
}

export const HiddenAriaLiveAnnouncement = (props: PropsWithChildren) => {
    return (
        <span
            aria-live="polite"
            css={css({
                position: "absolute",
                width: "1px",
                height: "1px",
                margin: "-1px",
                padding: "0",
                border: "0",
                clip: "rect(0 0 0 0)",
                overflow: "hidden"
            })}
        >
            {props.children}
        </span>
    );
};

/**
 * Ignore Algolia analytics for Pollinator user agent.
 */
export function shouldEnableAlgoliaAnalytics() {
    return !(
        typeof window !== "undefined" &&
        window.navigator.userAgent.includes("Pollinator")
    );
}
